/**
 * Hook para sincronização automática com Google Calendar
 *
 * Funcionalidades:
 * - Escuta mudanças em tarefas via Supabase Realtime
 * - Sincroniza periodicamente (a cada 15 minutos)
 * - Só funciona se o usuário tiver Google Calendar conectado e auto-sync ativado
 */

import { useCallback, useEffect, useRef } from 'react'

import { useAuth } from '@features/sistema/hooks/useAuth'
import { supabase } from '@features/sistema/services/supabaseClient'
import {
  getGoogleCalendarConnectionStatus,
  isAutoSyncEnabled,
  setAutoSyncEnabled as setAutoSyncStorage,
  syncTasksToGoogleCalendar} from '@features/tarefas/services/googleCalendarService'

import { RealtimePostgresChangesPayload } from '@supabase/supabase-js'

// Intervalo de sincronização periódica (15 minutos em ms)
const SYNC_INTERVAL = 15 * 60 * 1000

// Debounce para evitar múltiplas sincronizações em sequência
const DEBOUNCE_DELAY = 5000 // 5 segundos

interface TarefaPayload {
  id: string
  data_inicio_tem_hora?: boolean
  responsavel_id?: string
  status?: string
  ativo?: boolean
}

export function useGoogleCalendarAutoSync() {
  const { user } = useAuth()
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const periodicIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastSyncRef = useRef<number>(0)
  const isConnectedRef = useRef<boolean>(false)
  const autoSyncEnabledRef = useRef<boolean>(false)

  // Função para executar sincronização com debounce
  const debouncedSync = useCallback(async () => {
    if (!user?.id || !isConnectedRef.current || !autoSyncEnabledRef.current) {
      return
    }

    // Evitar sincronizações muito frequentes
    const now = Date.now()
    if (now - lastSyncRef.current < DEBOUNCE_DELAY) {
      // Já tem uma sincronização agendada ou muito recente
      return
    }

    // Limpar timeout anterior se existir
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current)
    }

    // Agendar sincronização com debounce
    syncTimeoutRef.current = setTimeout(async () => {
      try {
        console.log('🔄 [AutoSync] Iniciando sincronização automática...')
        lastSyncRef.current = Date.now()
        const result = await syncTasksToGoogleCalendar(user.id)

        if (result.success) {
          console.log(`✅ [AutoSync] Sincronização concluída: ${result.count} tarefa(s)`)
        } else {
          console.warn('⚠️ [AutoSync] Sincronização com erros:', result.errors)
        }
      } catch (error) {
        console.error('❌ [AutoSync] Erro na sincronização:', error)
      }
    }, DEBOUNCE_DELAY)
  }, [user?.id])

  // Verificar se deve sincronizar baseado na mudança da tarefa
  const shouldSync = useCallback((payload: TarefaPayload): boolean => {
    if (!user?.id) return false

    // Verificar se a tarefa tem hora definida
    if (!payload.data_inicio_tem_hora) return false

    // Verificar se o usuário é responsável direto
    // (para responsáveis via tabela tarefas_responsaveis, a sincronização periódica cuidará)
    if (payload.responsavel_id === user.id) return true

    // Se não conseguimos determinar, sincronizar por segurança
    return true
  }, [user?.id])

  // Handler para mudanças no Realtime
  const handleRealtimeChange = useCallback(
    (payload: RealtimePostgresChangesPayload<TarefaPayload>) => {
      console.log('📡 [AutoSync] Mudança detectada:', payload.eventType)

      if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
        const tarefa = payload.new as TarefaPayload

        if (shouldSync(tarefa)) {
          console.log('📅 [AutoSync] Tarefa elegível para sincronização:', tarefa.id)
          debouncedSync()
        }
      }
    },
    [shouldSync, debouncedSync]
  )

  // Sincronização periódica
  const startPeriodicSync = useCallback(() => {
    if (periodicIntervalRef.current) {
      clearInterval(periodicIntervalRef.current)
    }

    // Executar sincronização periódica
    periodicIntervalRef.current = setInterval(async () => {
      if (!isConnectedRef.current || !autoSyncEnabledRef.current) {
        return
      }

      console.log('⏰ [AutoSync] Sincronização periódica iniciada')
      await debouncedSync()
    }, SYNC_INTERVAL)

    console.log('⏰ [AutoSync] Sincronização periódica configurada (intervalo: 15 min)')
  }, [debouncedSync])

  // Parar sincronização periódica
  const stopPeriodicSync = useCallback(() => {
    if (periodicIntervalRef.current) {
      clearInterval(periodicIntervalRef.current)
      periodicIntervalRef.current = null
      console.log('⏹️ [AutoSync] Sincronização periódica parada')
    }
  }, [])

  // Verificar status da conexão e configurar listeners
  useEffect(() => {
    if (!user?.id) return

    let channel: ReturnType<typeof supabase.channel> | null = null

    const setupAutoSync = async () => {
      // Verificar conexão com Google Calendar
      const status = await getGoogleCalendarConnectionStatus()
      isConnectedRef.current = status.hasToken
      autoSyncEnabledRef.current = isAutoSyncEnabled()

      if (!status.hasToken) {
        console.log('📵 [AutoSync] Google Calendar não conectado, auto-sync desativado')
        return
      }

      if (!autoSyncEnabledRef.current) {
        console.log('🔕 [AutoSync] Auto-sync desativado pelo usuário')
        return
      }

      console.log('🚀 [AutoSync] Configurando sincronização automática...')

      // Configurar listener do Realtime
      channel = supabase
        .channel('tarefas-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'tarefas'
          },
          handleRealtimeChange
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            console.log('📡 [AutoSync] Conectado ao Supabase Realtime')
          }
        })

      // Iniciar sincronização periódica
      startPeriodicSync()

      // Fazer uma sincronização inicial
      console.log('🔄 [AutoSync] Executando sincronização inicial...')
      await debouncedSync()
    }

    setupAutoSync()

    // Cleanup
    return () => {
      if (channel) {
        channel.unsubscribe()
        console.log('📴 [AutoSync] Desconectado do Supabase Realtime')
      }
      stopPeriodicSync()
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
      }
    }
  }, [user?.id, handleRealtimeChange, startPeriodicSync, stopPeriodicSync, debouncedSync])

  // Função para atualizar o estado de auto-sync
  const setAutoSyncEnabled = useCallback((enabled: boolean) => {
    autoSyncEnabledRef.current = enabled
    setAutoSyncStorage(enabled)

    if (enabled && isConnectedRef.current) {
      console.log('✅ [AutoSync] Auto-sync ativado')
      startPeriodicSync()
      debouncedSync()
    } else {
      console.log('🔕 [AutoSync] Auto-sync desativado')
      stopPeriodicSync()
    }
  }, [startPeriodicSync, stopPeriodicSync, debouncedSync])

  // Função para forçar sincronização manual
  const syncNow = useCallback(async () => {
    if (!user?.id) return { success: false, count: 0, errors: ['Usuário não autenticado'] }

    try {
      console.log('🔄 [AutoSync] Sincronização manual iniciada')
      lastSyncRef.current = Date.now()
      const result = await syncTasksToGoogleCalendar(user.id)
      return result
    } catch (error) {
      console.error('❌ [AutoSync] Erro na sincronização manual:', error)
      return { success: false, count: 0, errors: ['Erro ao sincronizar'] }
    }
  }, [user?.id])

  return {
    setAutoSyncEnabled,
    syncNow,
    isAutoSyncEnabled: autoSyncEnabledRef.current
  }
}
